import { api } from '../../../shared/lib/axios';

/**
 * API client de Oportunidades Comerciais (US-COM-004, change
 * `gestao-oportunidade-comerciais`). Fatia 9: MVP de leitura —
 * list, findOne, stages, indicators. Mutations de escrita (create,
 * update, move-stage, desfechos, subrecursos) entram nas fatias
 * seguintes junto com formulário e modais.
 */

export type OportunidadeStatus = 'aberta' | 'ganha' | 'perdida' | 'suspensa' | 'cancelada';

export type Prioridade = 'baixa' | 'media' | 'alta' | 'critica';

export interface OpportunityStage {
  id: string;
  code: string;
  label: string;
  order: number;
  defaultProbability: number;
  isRequired: boolean;
  outcomeType: OportunidadeStatus;
  active: boolean;
}

export interface OportunidadeListItem {
  id: string;
  codigo: string | null;
  nome: string;
  clientId: string;
  responsavelId: string;
  stageId: string;
  status: OportunidadeStatus;
  prioridade: Prioridade;
  valorEstimado: string | null;
  valorPonderado: string | null;
  moeda: string;
  probabilidade: number | null;
  expectedClosingDate: string | null;
  nextActionDate: string | null;
  isConfidential: boolean;
  isStalled: boolean;
  isDraft: boolean;
  updatedAt: string;
}

export interface Oportunidade extends OportunidadeListItem {
  unidadeId: string | null;
  contatoPrincipalId: string;
  origemId: string;
  tipoId: string;
  custoEstimado: string | null;
  margemValor: string | null;
  margemPercentual: string | null;
  marginBelowThreshold: boolean;
  expectedClosingOverdueJustification: string | null;
  nextActionDate: string | null;
  nextActionDescription: string | null;
  nextActionOverdue: boolean;
  produtoServico: string;
  necessidade: string;
  escopo: string | null;
  observacoes: string | null;
  confirmedNotDuplicate: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
}

export interface ListOportunidadesParams {
  search?: string;
  clientId?: string;
  unidadeId?: string;
  responsavelId?: string;
  stageId?: string;
  origemId?: string;
  tipoId?: string;
  status?: OportunidadeStatus;
  prioridade?: Prioridade;
  openedFrom?: string;
  openedTo?: string;
  expectedClosingFrom?: string;
  expectedClosingTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  isDraft?: 'true' | 'false' | 'all';
  page?: number;
  limit?: number;
  offset?: number;
}

export interface ListOportunidadesResponse {
  data: OportunidadeListItem[];
  pagination: { limit: number; offset: number; count: number; page: number; hasNext: boolean };
}

export const OPORTUNIDADES_KEYS = {
  all: ['oportunidades'] as const,
  list: (params?: ListOportunidadesParams) => ['oportunidades', 'list', params] as const,
  detail: (id: string) => ['oportunidades', 'detail', id] as const,
  stages: () => ['oportunidades', 'stages'] as const,
  indicator: (name: string, params?: Record<string, unknown>) =>
    ['oportunidades', 'indicators', name, params] as const,
};

/**
 * Reencaminha filtros para query string. Ignora `undefined`/`null`/''
 * — reset explícito de filtro se faz via omissão do campo.
 */
function toQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// -------- Listagem e detalhe -----------------------------------------

export async function listarOportunidades(
  params?: ListOportunidadesParams,
): Promise<ListOportunidadesResponse> {
  const { data } = await api.get<ListOportunidadesResponse>(
    `/opportunities${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarOportunidade(id: string): Promise<Oportunidade> {
  const { data } = await api.get<Oportunidade>(`/opportunities/${id}`);
  return data;
}

// -------- Mutations --------------------------------------------------

export interface CreateOportunidadePayload {
  nome: string;
  clientId: string;
  unidadeId?: string;
  contatoPrincipalId: string;
  responsavelId: string;
  origemId: string;
  tipoId: string;
  stageId?: string;
  prioridade: Prioridade;
  valorEstimado?: string;
  custoEstimado?: string;
  moeda?: string;
  probabilidade?: number;
  expectedClosingDate?: string;
  nextActionDate?: string;
  nextActionDescription?: string;
  produtoServico: string;
  necessidade: string;
  escopo?: string;
  isConfidential?: boolean;
  observacoes?: string;
  confirmedNotDuplicate?: boolean;
}

export type UpdateOportunidadePayload = Partial<
  Omit<CreateOportunidadePayload, 'confirmedNotDuplicate'>
> & {
  expectedClosingOverdueJustification?: string;
};

/**
 * Payload de conflito por duplicidade — 409 emitido pelo
 * `OpportunitiesService.checkDuplicate`.
 */
export interface OportunidadeDuplicataCandidate {
  id: string;
  codigo: string | null;
  nome: string;
}

export class OportunidadeDuplicataError extends Error {
  constructor(public readonly candidate: OportunidadeDuplicataCandidate) {
    super('Já existe oportunidade aberta similar para este cliente.');
    this.name = 'OportunidadeDuplicataError';
  }
}

export async function criarOportunidade(
  payload: CreateOportunidadePayload,
): Promise<Oportunidade> {
  try {
    const { data } = await api.post<Oportunidade>('/opportunities', payload);
    return data;
  } catch (err) {
    const e = err as {
      response?: { status?: number; data?: { code?: string; candidate?: OportunidadeDuplicataCandidate } };
    };
    if (
      e.response?.status === 409 &&
      e.response.data?.code === 'OPPORTUNITY_DUPLICATE_SUGGESTED' &&
      e.response.data?.candidate
    ) {
      throw new OportunidadeDuplicataError(e.response.data.candidate);
    }
    throw err;
  }
}

export async function salvarRascunho(
  payload: CreateOportunidadePayload,
): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>('/opportunities/drafts', payload);
  return data;
}

export async function atualizarOportunidade(
  id: string,
  payload: UpdateOportunidadePayload,
): Promise<Oportunidade> {
  const { data } = await api.patch<Oportunidade>(`/opportunities/${id}`, payload);
  return data;
}

// -------- Histórico (change_history) --------------------------------

export interface OpportunityHistoryEntry {
  id: string;
  opportunityId: string;
  userId: string | null;
  action: string;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  changedFields: string[];
  reason: string | null;
  context: Record<string, unknown> | null;
  createdAt: string;
}

export interface ListHistoryResponse {
  data: OpportunityHistoryEntry[];
  pagination: { limit: number; offset: number; count: number };
}

export async function listarHistorico(
  id: string,
  params?: { limit?: number; offset?: number },
): Promise<ListHistoryResponse> {
  const { data } = await api.get<ListHistoryResponse>(
    `/opportunities/${id}/history${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

// -------- Interações (leitura + escrita) -----------------------------

export type OpportunityInteractionTipo =
  | 'ligacao'
  | 'reuniao'
  | 'email'
  | 'whatsapp'
  | 'apresentacao'
  | 'visita'
  | 'envio_material'
  | 'negociacao'
  | 'alinhamento_tecnico'
  | 'follow_up'
  | 'outro';

export interface OpportunityInteraction {
  id: string;
  opportunityId: string;
  tipo: OpportunityInteractionTipo;
  occurredAt: string;
  contatoClienteId: string | null;
  summary: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInteractionPayload {
  tipo: OpportunityInteractionTipo;
  occurredAt: string;
  contatoClienteId?: string;
  summary: string;
  participantUserIds?: string[];
}

export async function listarInteracoes(id: string): Promise<OpportunityInteraction[]> {
  const { data } = await api.get<{ data: OpportunityInteraction[] }>(
    `/opportunities/${id}/interactions`,
  );
  return data.data;
}

export async function criarInteracao(
  id: string,
  payload: CreateInteractionPayload,
): Promise<OpportunityInteraction> {
  const { data } = await api.post<OpportunityInteraction>(
    `/opportunities/${id}/interactions`,
    payload,
  );
  return data;
}

export async function removerInteracao(
  opportunityId: string,
  interactionId: string,
): Promise<void> {
  await api.delete(
    `/opportunities/${opportunityId}/interactions/${interactionId}`,
  );
}

// -------- Participantes internos ------------------------------------

export interface OpportunityParticipant {
  id: string;
  opportunityId: string;
  userId: string;
  roleId: string;
  addedAt: string;
  addedBy: string | null;
  removedAt: string | null;
  removedBy: string | null;
}

export interface AddParticipantPayload {
  userId: string;
  roleId: string;
}

export async function listarParticipantes(id: string): Promise<OpportunityParticipant[]> {
  const { data } = await api.get<{ data: OpportunityParticipant[] }>(
    `/opportunities/${id}/participants`,
  );
  return data.data;
}

export async function adicionarParticipante(
  id: string,
  payload: AddParticipantPayload,
): Promise<OpportunityParticipant> {
  const { data } = await api.post<OpportunityParticipant>(
    `/opportunities/${id}/participants`,
    payload,
  );
  return data;
}

export async function removerParticipante(
  opportunityId: string,
  participantId: string,
): Promise<void> {
  await api.delete(`/opportunities/${opportunityId}/participants/${participantId}`);
}

// -------- Concorrentes ----------------------------------------------

export interface OpportunityCompetitorLink {
  id: string;
  opportunityId: string;
  competitorId: string;
  competitorName: string | null;
  solution: string | null;
  positioning: string | null;
  priceRange: string | null;
  strongPoint: string | null;
  weakPoint: string | null;
  risk: 'baixo' | 'medio' | 'alto' | null;
  isWinner: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AttachCompetitorPayload {
  /** Reaproveita competitor existente. */
  competitorId?: string;
  /** Ou cria sob demanda case-insensitive. */
  competitorName?: string;
  solution?: string;
  positioning?: string;
  priceRange?: string;
  strongPoint?: string;
  weakPoint?: string;
  risk?: 'baixo' | 'medio' | 'alto';
}

export type UpdateCompetitorLinkPayload = Omit<
  AttachCompetitorPayload,
  'competitorId' | 'competitorName'
>;

export async function listarConcorrentes(id: string): Promise<OpportunityCompetitorLink[]> {
  const { data } = await api.get<{ data: OpportunityCompetitorLink[] }>(
    `/opportunities/${id}/competitors`,
  );
  return data.data;
}

export async function vincularConcorrente(
  id: string,
  payload: AttachCompetitorPayload,
): Promise<OpportunityCompetitorLink> {
  const { data } = await api.post<OpportunityCompetitorLink>(
    `/opportunities/${id}/competitors`,
    payload,
  );
  return data;
}

export async function atualizarConcorrente(
  opportunityId: string,
  linkId: string,
  payload: UpdateCompetitorLinkPayload,
): Promise<OpportunityCompetitorLink> {
  const { data } = await api.patch<OpportunityCompetitorLink>(
    `/opportunities/${opportunityId}/competitors/${linkId}`,
    payload,
  );
  return data;
}

export async function desvincularConcorrente(
  opportunityId: string,
  linkId: string,
): Promise<void> {
  await api.delete(`/opportunities/${opportunityId}/competitors/${linkId}`);
}

// -------- Anexos ----------------------------------------------------

export interface OpportunityAttachment {
  id: string;
  opportunityId: string;
  filename: string;
  mimeType: string | null;
  sizeBytes: string | null;
  kind: string | null;
  description: string | null;
  isReusableOnProposal: boolean;
  uploadedBy: string | null;
  uploadedAt: string;
}

export interface UploadAttachmentMeta {
  kind?: string;
  description?: string;
  isReusableOnProposal?: boolean;
}

export async function listarAnexos(id: string): Promise<OpportunityAttachment[]> {
  const { data } = await api.get<{ data: OpportunityAttachment[] }>(
    `/opportunities/${id}/attachments`,
  );
  return data.data;
}

export async function enviarAnexo(
  id: string,
  file: File,
  meta: UploadAttachmentMeta,
): Promise<OpportunityAttachment> {
  const form = new FormData();
  form.append('file', file);
  if (meta.kind) form.append('kind', meta.kind);
  if (meta.description) form.append('description', meta.description);
  if (meta.isReusableOnProposal !== undefined)
    form.append('isReusableOnProposal', String(meta.isReusableOnProposal));
  const { data } = await api.post<OpportunityAttachment>(
    `/opportunities/${id}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function removerAnexo(
  opportunityId: string,
  attachmentId: string,
): Promise<void> {
  await api.delete(`/opportunities/${opportunityId}/attachments/${attachmentId}`);
}

/**
 * URL absoluta do download. O axios do app já injeta o token de auth
 * como cookie/header; deixamos o browser navegar direto (mais simples
 * que gerenciar blob/stream via React).
 */
export function urlDownloadAnexo(opportunityId: string, attachmentId: string): string {
  const base = (api.defaults.baseURL ?? '').replace(/\/+$/, '');
  return `${base}/opportunities/${opportunityId}/attachments/${attachmentId}/download`;
}

// -------- Domínio: papéis de participante ---------------------------

export async function listarPapeisParticipante(): Promise<DomainOption[]> {
  const { data } = await api.get<{ data: DomainOption[] }>('/opportunity-participant-roles');
  return data.data;
}

// -------- Movimentação de etapa --------------------------------------

export interface MoveStagePayload {
  toStageId: string;
  observation?: string;
  justification?: string;
  overrideProbability?: number;
}

export async function moverEtapa(
  id: string,
  payload: MoveStagePayload,
): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(
    `/opportunities/${id}/move-stage`,
    payload,
  );
  return data;
}

// -------- Desfechos e ações ------------------------------------------

export type TipoEvidencia =
  | 'proposta_assinada'
  | 'contrato_assinado'
  | 'pedido_compra'
  | 'autorizacao_formal'
  | 'email_aceite'
  | 'outro';

export interface WinPayload {
  valorFinal: string;
  dataAceite: string;
  tipoEvidencia: TipoEvidencia;
  anexoEvidenciaId: string;
  previsaoInicio?: string;
  responsavelContinuidadeId?: string;
}

export interface LosePayload {
  motivoId: string;
  justificativa: string;
  concorrenteVencedorId?: string;
  valorFinalConhecido?: string;
  possibilidadeRetomada: boolean;
  licoesAprendidas?: string;
}

export interface SuspendPayload {
  motivo: string;
  dataSuspensao?: string;
  previsaoRetomada: string;
  responsavelAcompanhamentoId?: string;
}

export interface ReactivatePayload {
  newExpectedClosingDate: string;
  nextActionDate: string;
  nextActionDescription: string;
}

export interface CancelPayload {
  justificativa: string;
}

export interface TransferPayload {
  newResponsavelId: string;
  motivo: string;
}

export interface ApproveMarginPayload {
  justification: string;
}

export async function ganharOportunidade(id: string, payload: WinPayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/win`, payload);
  return data;
}

export async function perderOportunidade(id: string, payload: LosePayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/lose`, payload);
  return data;
}

export async function suspenderOportunidade(id: string, payload: SuspendPayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/suspend`, payload);
  return data;
}

export async function reativarOportunidade(id: string, payload: ReactivatePayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/reactivate`, payload);
  return data;
}

export async function cancelarOportunidade(id: string, payload: CancelPayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/cancel`, payload);
  return data;
}

export async function transferirOportunidade(id: string, payload: TransferPayload): Promise<Oportunidade> {
  const { data } = await api.post<Oportunidade>(`/opportunities/${id}/transfer`, payload);
  return data;
}

export async function aprovarMargem(
  id: string,
  payload: ApproveMarginPayload,
): Promise<unknown> {
  const { data } = await api.post(`/opportunities/${id}/approve-margin`, payload);
  return data;
}

// -------- Conversão em proposta (RN-COM-018/019) ---------------------

export interface ProposeOpportunityPayload {
  /** Base Comercial vigente a aplicar. Opcional — corpo vazio preserva
      o comportamento sem Base (cliente sem Base vigente). */
  commercialBaseId?: string;
  /** Desconto pretendido — sujeito à alçada da Base (RN-COM-016). */
  descontoPercentual?: number;
}

export async function proporOportunidade(
  id: string,
  payload: ProposeOpportunityPayload = {},
): Promise<unknown> {
  const { data } = await api.post(`/opportunities/${id}/propose`, payload);
  return data;
}

// -------- Etapas do pipeline -----------------------------------------

export async function listarEtapas(includeInactive = false): Promise<OpportunityStage[]> {
  const { data } = await api.get<{ data: OpportunityStage[] }>(
    `/opportunity-stages${includeInactive ? '?includeInactive=true' : ''}`,
  );
  return data.data;
}

// -------- Domínios administráveis (leitura) --------------------------

export interface DomainOption {
  id: string;
  code: string;
  label: string;
  active: boolean;
  order: number;
}

export async function listarOrigens(): Promise<DomainOption[]> {
  const { data } = await api.get<{ data: DomainOption[] }>('/opportunity-origins');
  return data.data;
}

export async function listarTipos(): Promise<DomainOption[]> {
  const { data } = await api.get<{ data: DomainOption[] }>('/opportunity-types');
  return data.data;
}

export async function listarMotivosPerda(): Promise<DomainOption[]> {
  const { data } = await api.get<{ data: DomainOption[] }>('/opportunity-lost-reasons');
  return data.data;
}

// -------- Indicadores (subset — dashboard usa) -----------------------

export interface PipelineSummary {
  count: number;
  total: string;
  weighted: string;
}

export interface ForecastBucket {
  bucket: string;
  count: number;
  total: string;
}

export interface ForecastResponse {
  unit: 'month' | 'quarter' | 'year';
  weighted: boolean;
  buckets: ForecastBucket[];
}

export interface ConversionRateResponse {
  period: { from?: string; to?: string };
  totals: { won: number; lost: number; cancelled: number };
  globalRate: number;
}

export async function buscarIndicadorPipeline(): Promise<PipelineSummary> {
  const { data } = await api.get<PipelineSummary>('/opportunities/indicators/pipeline');
  return data;
}

export async function buscarIndicadorForecastMensal(params: {
  from?: string;
  to?: string;
  weighted?: boolean;
}): Promise<ForecastResponse> {
  const { data } = await api.get<ForecastResponse>(
    `/opportunities/indicators/forecast/monthly${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function buscarIndicadorConversao(params: {
  from?: string;
  to?: string;
}): Promise<ConversionRateResponse> {
  const { data } = await api.get<ConversionRateResponse>(
    `/opportunities/indicators/conversion-rate${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export interface StageAggregate {
  stageId: string;
  count: number;
  total: string;
  weighted: string;
}

export async function buscarIndicadorPorEtapa(): Promise<StageAggregate[]> {
  const { data } = await api.get<{ data: StageAggregate[] }>('/opportunities/indicators/by-stage');
  return data.data;
}

/**
 * Health-check endpoints devolvem listas de oportunidades — o dashboard
 * usa só `.length` para os KPIs, mas o payload completo permite render
 * "top 5 paradas" no drill-down.
 */
export async function buscarIndicadorStalled(): Promise<OportunidadeListItem[]> {
  const { data } = await api.get<{ data: OportunidadeListItem[] }>('/opportunities/indicators/stalled');
  return data.data;
}

export async function buscarIndicadorSemProximaAcao(): Promise<OportunidadeListItem[]> {
  const { data } = await api.get<{ data: OportunidadeListItem[] }>(
    '/opportunities/indicators/without-next-action',
  );
  return data.data;
}

export async function buscarIndicadorFechamentoVencido(): Promise<OportunidadeListItem[]> {
  const { data } = await api.get<{ data: OportunidadeListItem[] }>(
    '/opportunities/indicators/expected-closing-overdue',
  );
  return data.data;
}
